'use strict';
/* ==========================================================================
   levels/content.js — the lesson content for the MATHS and QUIZ sections.
   Pure data, authored by the architect; levels/maths.js and levels/quiz.js
   render it. Physics correctness is checked by tools/contentcheck.js, which
   recomputes every answer from the numbers in the question — change a number
   here and that check tells you if the answer no longer follows.

   Conventions (stated to the player in the maths intro):
   • real units: newtons (N), kilograms (kg), metres per second (m/s), m/s²
   • g = 10 m/s², so weight in newtons = mass in kg × 10
   • directions: right/up positive; "net force" means the size of the resultant

   Figure vocabulary (maths.js draws these as small collage diagrams):
   { type:'forces', body:'crate'|'book'|'puck'|'rope'|'apple', arrows:[{dir:'left'|'right'|'up'|'down', F, label?}] }
     F may be '?' (the unknown the exercise asks for) — draw it as a '?' label, never a number.
     An arrow may say on:'earth' — a law-3 pair acts on TWO bodies, so that arrow must be drawn on
     the Earth, not on the apple (drawing both on one body teaches the classic law-3 mistake).
   { type:'push',   img, m, F, friction? }   img = an engraved cut-out name (teacup, elephant, piano, cat,
                    apple, anvil, rocket, gent, globe) OR a paper prop drawn in code: ball, cart, car, box
   { type:'pair',   left:{label, m}, right:{label, m}, F }        two bodies pushing apart
   { type:'wall',   F, m? }                                         person pushing a wall
   { type:'rocket', thrust, weight }
   { type:'compare', a:{img, m}, b:{img, m}, F }                   same force, two masses
   ========================================================================== */
window.CONTENT = {
  maths: {
    g: 10,
    intro: [
      'Time to put numbers on it.',
      'We use real units now: newtons (N) for force, kilograms (kg) for mass, m/s² for acceleration.',
      'Weight = mass × 10 (on Earth, g is about 10 m/s²).',
    ],
    chapters: [
      /* ------------------------------------------------------------------ LAW 1 */
      {
        id: 'law1', law: 1, title: 'BALANCED FORCES', subtitle: 'law 1 · inertia',
        formula: 'net force = 0   means   velocity stays the same',
        idea: [
          'Add up all the forces. Opposite directions subtract.',
          'If they cancel (net force = 0), nothing changes: still stays still, moving keeps moving.',
        ],
        worked: {
          q: 'A crate is pushed right with 40 N and pushed left with 40 N. It is already sliding at 2 m/s. What happens to its speed?',
          figure: { type: 'forces', body: 'crate', arrows: [{ dir: 'right', F: 40 }, { dir: 'left', F: 40 }] },
          steps: ['net force = 40 N − 40 N = 0 N', 'no net force, so no acceleration', 'so it keeps sliding at 2 m/s'],
        },
        exercises: [
          {
            id: 'e1_1', q: 'Tug of war: team A pulls left with 150 N, team B pulls right with 120 N. How big is the net force?',
            unit: 'N', answer: 30, tol: 0,
            figure: { type: 'forces', body: 'rope', arrows: [{ dir: 'left', F: 150, label: 'team A' }, { dir: 'right', F: 120, label: 'team B' }] },
            hint: 'Opposite directions: subtract the smaller pull from the bigger one.',
            solution: ['net force = 150 N − 120 N', '= 30 N, towards team A (left)'],
          },
          {
            id: 'e1_2', q: 'A puck slides on frictionless ice at 6 m/s. Nothing pushes or pulls it along the ice. How fast is it going 10 seconds later?',
            unit: 'm/s', answer: 6, tol: 0,
            figure: { type: 'forces', body: 'puck', arrows: [] },
            hint: 'Is there any force along the ice to change its speed?',
            solution: ['frictionless ice: no force along the ice', 'net force = 0, so the velocity does not change', 'still 6 m/s — the 10 seconds is a red herring'],
          },
          {
            id: 'e1_3', q: 'A book rests on a table. Its weight pulls it down with 8 N. How hard does the table push up on it?',
            unit: 'N', answer: 8, tol: 0,
            figure: { type: 'forces', body: 'book', arrows: [{ dir: 'down', F: 8, label: 'weight' }, { dir: 'up', F: '?', label: 'table' }] },
            hint: 'The book is not moving. What must the net force be?',
            solution: ['the book stays still, so net force = 0', 'so the up push must cancel the 8 N weight', 'the table pushes up with 8 N'],
          },
        ],
      },
      /* ------------------------------------------------------------------ LAW 2 */
      {
        id: 'law2', law: 2, title: 'F = m × a', subtitle: 'law 2 · force, mass, acceleration',
        formula: 'F = m × a      a = F ÷ m      m = F ÷ a',
        idea: [
          'Bigger force: bigger acceleration. Bigger mass: smaller acceleration.',
          'Use the NET force: if friction pulls back, subtract it first.',
        ],
        worked: {
          q: 'A 2 kg ball is kicked with a force of 10 N. What is its acceleration?',
          figure: { type: 'push', img: 'ball', m: 2, F: 10 },
          steps: ['a = F ÷ m', 'a = 10 N ÷ 2 kg', 'a = 5 m/s²'],
        },
        exercises: [
          {
            id: 'e2_1', q: 'A 3 kg cart is pushed with a force of 12 N. What is its acceleration?',
            unit: 'm/s²', answer: 4, tol: 0,
            figure: { type: 'push', img: 'cart', m: 3, F: 12 },
            hint: 'a = F ÷ m',
            solution: ['a = F ÷ m', 'a = 12 N ÷ 3 kg', 'a = 4 m/s²'],
          },
          {
            id: 'e2_2', q: 'What force gives a 1200 kg car an acceleration of 2 m/s²?',
            unit: 'N', answer: 2400, tol: 0,
            figure: { type: 'push', img: 'car', m: 1200, F: '?' },
            hint: 'F = m × a',
            solution: ['F = m × a', 'F = 1200 kg × 2 m/s²', 'F = 2400 N'],
          },
          {
            id: 'e2_3', q: 'A 50 N push makes a box accelerate at 5 m/s². What is the mass of the box?',
            unit: 'kg', answer: 10, tol: 0,
            figure: { type: 'push', img: 'box', m: '?', F: 50 },
            hint: 'm = F ÷ a',
            solution: ['m = F ÷ a', 'm = 50 N ÷ 5 m/s²', 'm = 10 kg'],
          },
          {
            id: 'e2_4', q: 'You push a 4 kg box with 30 N, but friction pulls back with 10 N. What is its acceleration?',
            unit: 'm/s²', answer: 5, tol: 0,
            figure: { type: 'push', img: 'box', m: 4, F: 30, friction: 10 },
            hint: 'Find the NET force first, then divide by the mass.',
            solution: ['net force = 30 N − 10 N = 20 N', 'a = net F ÷ m = 20 N ÷ 4 kg', 'a = 5 m/s²'],
          },
          {
            id: 'e2_5', q: 'The same 600 N push acts on a 1 kg teacup and on a 100 kg elephant. How many times bigger is the teacup\'s acceleration?',
            unit: 'times', answer: 100, tol: 0,
            figure: { type: 'compare', a: { img: 'teacup', m: 1 }, b: { img: 'elephant', m: 100 }, F: 600 },
            hint: 'Work out each acceleration with a = F ÷ m, then divide one by the other.',
            solution: ['teacup: a = 600 ÷ 1 = 600 m/s²', 'elephant: a = 600 ÷ 100 = 6 m/s²', '600 ÷ 6 = 100 times bigger'],
          },
        ],
      },
      /* ------------------------------------------------------------------ LAW 3 */
      {
        id: 'law3', law: 3, title: 'FORCE PAIRS', subtitle: 'law 3 · action and reaction',
        formula: 'A pushes B,  B pushes A back:  same size, opposite way',
        idea: [
          'Forces always come in pairs, acting on two DIFFERENT objects.',
          'The pair has equal size — but each object accelerates by its own a = F ÷ m.',
        ],
        worked: {
          q: 'A 50 kg skater pushes on a wall with 100 N. How hard does the wall push back, and what is the skater\'s acceleration?',
          figure: { type: 'wall', F: 100, m: 50 },
          steps: ['the wall pushes back with 100 N (law 3)', 'skater: a = F ÷ m = 100 N ÷ 50 kg', 'a = 2 m/s², away from the wall'],
        },
        exercises: [
          {
            id: 'e3_1', q: 'You push on a wall with a force of 60 N. How hard does the wall push on you?',
            unit: 'N', answer: 60, tol: 0,
            figure: { type: 'wall', F: 60 },
            hint: 'Law 3: the pair is always the same size.',
            solution: ['action: you push the wall with 60 N', 'reaction: the wall pushes you with 60 N, the other way'],
          },
          {
            id: 'e3_2', q: 'Two skaters push off each other with 120 N. Skater A has a mass of 60 kg. What is A\'s acceleration?',
            unit: 'm/s²', answer: 2, tol: 0,
            figure: { type: 'pair', left: { label: 'A', m: 60 }, right: { label: 'B', m: 40 }, F: 120 },
            hint: 'A feels the full 120 N. Use a = F ÷ m.',
            solution: ['each skater feels 120 N (law 3)', 'A: a = 120 N ÷ 60 kg', 'a = 2 m/s²'],
          },
          {
            id: 'e3_3', q: 'Same push (120 N). Skater B has a mass of 40 kg. What is B\'s acceleration?',
            unit: 'm/s²', answer: 3, tol: 0,
            figure: { type: 'pair', left: { label: 'A', m: 60 }, right: { label: 'B', m: 40 }, F: 120 },
            hint: 'Same force as A, smaller mass.',
            solution: ['B also feels 120 N — same size as A (law 3)', 'B: a = 120 N ÷ 40 kg', 'a = 3 m/s² — lighter, so faster'],
          },
          {
            id: 'e3_4', q: 'A rocket pushes its gas down with 3000 N, so the gas pushes the rocket up with 3000 N. The rocket weighs 1000 N (mass 100 kg). What is its upward acceleration?',
            unit: 'm/s²', answer: 20, tol: 0,
            figure: { type: 'rocket', thrust: 3000, weight: 1000 },
            hint: 'Net force = up push − weight. Then a = F ÷ m.',
            solution: ['net force = 3000 N − 1000 N = 2000 N up', 'a = 2000 N ÷ 100 kg', 'a = 20 m/s² upward'],
          },
          {
            id: 'e3_5', q: 'The Earth pulls an apple down with a force of 1 N. How hard does the apple pull the Earth up?',
            unit: 'N', answer: 1, tol: 0,
            figure: { type: 'forces', body: 'apple', arrows: [{ dir: 'down', F: 1, label: 'Earth on apple', on: 'apple' }, { dir: 'up', F: '?', label: 'apple on Earth', on: 'earth' }] },
            hint: 'It\'s a force pair. Does the size depend on which one is bigger?',
            solution: ['it is a law-3 pair, so the same size', 'the apple pulls the Earth with 1 N', '(the Earth barely moves because its mass is enormous: a = F ÷ m)'],
          },
        ],
      },
    ],
  },

  quiz: {
    title: 'THE BIG QUIZ',
    pass: 7,        // out of 10 = "passed" stamp
    questions: [
      { id: 'q1', law: 1, q: 'A book is resting on a table. What is the net force on it?',
        options: ['zero', 'its weight, pulling down', 'the table\'s push, pointing up'], correct: 0,
        explain: 'It isn\'t accelerating, so the forces cancel: net force = 0.' },
      { id: 'q2', law: 1, q: 'A puck is sliding on perfectly frictionless ice. What does it do?',
        options: ['slows down and stops', 'keeps the same speed', 'speeds up'], correct: 1,
        explain: 'No net force means no change in velocity. It keeps going at the same speed.' },
      { id: 'q3', law: 1, q: 'A bus brakes suddenly and the passengers lurch forward. Why?',
        options: ['the bus pushes them forward', 'their bodies keep moving forward (inertia)', 'gravity pulls them forward'], correct: 1,
        explain: 'Inertia: their bodies keep moving at the old speed while the bus slows down.' },
      { id: 'q4', law: 2, q: 'The same force pushes a 2 kg cart and a 4 kg cart. The 2 kg cart\'s acceleration is…',
        options: ['half as big', 'the same', 'twice as big'], correct: 2,
        explain: 'a = F ÷ m: half the mass gives twice the acceleration.' },
      { id: 'q5', law: 2, q: 'A force of 20 N acts on a 5 kg box. What is its acceleration?',
        options: ['4 m/s²', '100 m/s²', '0.25 m/s²', '25 m/s²'], correct: 0,
        explain: 'a = F ÷ m = 20 N ÷ 5 kg = 4 m/s².' },
      { id: 'q6', law: 2, q: 'What force gives a 10 kg box an acceleration of 3 m/s²?',
        options: ['13 N', '3.3 N', '30 N', '7 N'], correct: 2,
        explain: 'F = m × a = 10 kg × 3 m/s² = 30 N.' },
      { id: 'q7', law: 3, q: 'You push on a wall with 50 N. How hard does the wall push on you?',
        options: ['0 N — walls don\'t push', '50 N', '100 N', 'it depends on how heavy the wall is'], correct: 1,
        explain: 'Law 3: the wall pushes back with the same 50 N, in the opposite direction.' },
      { id: 'q8', law: 3, q: 'How does a rocket push itself upward in empty space?',
        options: ['its exhaust pushes on the air', 'it pushes gas down, and the gas pushes it up', 'the launch pad keeps pushing it'], correct: 1,
        explain: 'Action: rocket pushes gas down. Reaction: gas pushes rocket up. No air needed.' },
      { id: 'q9', law: 3, q: 'The Earth pulls an apple with 1 N. How hard does the apple pull the Earth?',
        options: ['0 N', '1 N', 'a tiny bit less than 1 N', 'much more than 1 N'], correct: 1,
        explain: 'They are a force pair: exactly the same size, 1 N.' },
      { id: 'q10', law: 2, q: 'So why doesn\'t the Earth visibly move towards the apple?',
        options: ['there is no force on the Earth', 'its mass is enormous, so its acceleration is tiny', 'the two forces cancel out'], correct: 1,
        explain: 'Same 1 N, but a = F ÷ m with the Earth\'s huge mass gives an unimaginably small acceleration.' },
    ],
  },
};
